// src/pages/ProfilePicUpdate.js
import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import LookupUmat from "../components/LookupUmat";
import { apiFetch, apiFetchFormData } from "../utils/wkCommon";
import MessageLog from "../components/MessageLog";

const VIEWPORT_SIZE = 320; // ukuran kotak crop (square)

export default function ProfilePicUpdate() {
  const history = useHistory();
  const [lockedKodeUmat, setLockedKodeUmat] = useState(null);
  const [selected, setSelected] = useState(null);
  const [file, setFile] = useState(null);
  const [imgSrc, setImgSrc] = useState(null);
  const imgRef = useRef(null);
  const containerRef = useRef(null);

  // base display size (the rendered img size when scale = 1)
  const [baseDisp, setBaseDisp] = useState({ w: VIEWPORT_SIZE, h: VIEWPORT_SIZE });

  // transform state
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragRef = useRef({ dragging: false, startX: 0, startY: 0, startOffset: { x: 0, y: 0 } });

  // scale: 1 => fit-to-viewport (show whole image)
  const [scale, setScale] = useState(1);
  const [minScale, setMinScale] = useState(1);
  const [maxScale, setMaxScale] = useState(3);

  // rotation in degrees (0, 90, 180, 270)
  const [rotation, setRotation] = useState(0);

  const [processing, setProcessing] = useState(false);

  // single short status for button/brief UI messages
  const [status, setStatus] = useState("");

  // MessageLog ref for append-only logs
  const messageRef = useRef(null);

  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const kode = q.get("KodeUmat");
    if (kode) setLockedKodeUmat(kode);
  }, []);

  // When a file is selected: compute baseDisp and scales, reset transform
  function onFileChange(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      messageRef.current?.add("File bukan gambar", "warn");
      setStatus("File bukan gambar");
      return;
    }
    setFile(f);
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const dataUrl = ev.target.result;
      // load image to get natural size
      const img = await new Promise((res, rej) => {
        const i = new Image();
        i.onload = () => res(i);
        i.onerror = rej;
        i.src = dataUrl;
      });

      // compute base display so that whole image fits viewport:
      // if landscape (width >= height) -> fit width to VIEWPORT_SIZE
      // if portrait (height > width) -> fit height to VIEWPORT_SIZE
      let baseDispW, baseDispH;
      if (img.width >= img.height) {
        // width-based fit
        baseDispW = VIEWPORT_SIZE;
        baseDispH = Math.round((VIEWPORT_SIZE * img.height) / img.width);
      } else {
        // height-based fit
        baseDispH = VIEWPORT_SIZE;
        baseDispW = Math.round((VIEWPORT_SIZE * img.width) / img.height);
      }

      // make sure baseDisp minimum 1px
      baseDispW = Math.max(1, baseDispW);
      baseDispH = Math.max(1, baseDispH);

      // scale = 1 -> fit-to-viewport (shows whole image)
      const computedMinScale = 1;
      // maxScale so displayed size can reach natural size (no overscale beyond 1:1)
      const computedMaxScale = Math.max(
        1,
        img.width / baseDispW,
        img.height / baseDispH
      );

      setBaseDisp({ w: baseDispW, h: baseDispH });
      setMinScale(computedMinScale);
      setMaxScale(computedMaxScale);
      setScale(1);
      setOffset({ x: 0, y: 0 });
      setImgSrc(dataUrl);
      // clear short status (but keep logs)
      setStatus("");
    };
    reader.readAsDataURL(f);
  }

  // pointer drag handlers
  function onPointerDown(e) {
    if (!imgSrc) return;
    const evt = e.touches ? e.touches[0] : e;
    dragRef.current.dragging = true;
    dragRef.current.startX = evt.clientX;
    dragRef.current.startY = evt.clientY;
    dragRef.current.startOffset = { ...offset };
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
  }
  function onPointerMove(e) {
    if (!dragRef.current.dragging) return;
    const evt = e.touches ? e.touches[0] : e;
    const dx = evt.clientX - dragRef.current.startX;
    const dy = evt.clientY - dragRef.current.startY;
    setOffset({
      x: dragRef.current.startOffset.x + dx,
      y: dragRef.current.startOffset.y + dy,
    });
  }
  function onPointerUp() {
    dragRef.current.dragging = false;
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", onPointerUp);
  }

  // wheel zoom: change scale but clamp to [minScale, maxScale]
  function onWheel(e) {
    if (!imgSrc) return;
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.06 : 0.06;
    setScale((s) => {
      let nxt = +(s + delta).toFixed(3);
      if (nxt < minScale) nxt = minScale;
      if (nxt > maxScale) nxt = maxScale;
      return nxt;
    });
  }

  // produce cropped blob mapped exactly from viewport -> source
  async function produceCroppedBlob(outputSize = 256, maxBytes = 30 * 1024) {
    if (!imgSrc) throw new Error("Tidak ada gambar untuk diproses.");
    const img = await new Promise((res, rej) => {
      const i = new Image();
      i.onload = () => res(i);
      i.onerror = rej;
      i.src = imgSrc;
    });

    // displayed size at scale=1 is baseDisp, so at current scale:
    const displayedW = baseDisp.w * scale;
    const displayedH = baseDisp.h * scale;

    // image top-left in viewport coords
    const imgLeft = (VIEWPORT_SIZE - displayedW) / 2 + offset.x;
    const imgTop = (VIEWPORT_SIZE - displayedH) / 2 + offset.y;

    // mapping viewport -> source coordinates
    const scaleSrc = img.width / displayedW;
    const sx = (0 - imgLeft) * scaleSrc;
    const sy = (0 - imgTop) * scaleSrc;
    const sWidth = VIEWPORT_SIZE * scaleSrc;
    const sHeight = VIEWPORT_SIZE * scaleSrc;

    // draw to canvas
    const canvas = document.createElement("canvas");
    canvas.width = outputSize;
    canvas.height = outputSize;
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.clearRect(0, 0, outputSize, outputSize);

    // rotation in radians
    const rad = ((rotation % 360) * Math.PI) / 180;

    // Save, translate to center, rotate, draw then restore
    ctx.save();
    ctx.translate(outputSize / 2, outputSize / 2);
    ctx.rotate(rad);

    // drawImage will clip automatically if source rect is outside image
    ctx.drawImage(img, sx, sy, sWidth, sHeight, -outputSize / 2, -outputSize / 2, outputSize, outputSize);
    //ctx.drawImage(img, sx, sy, sWidth, sHeight, 0, 0, outputSize, outputSize);

    // compress to webp with fallback loop
    let quality = 0.8;
    const minQuality = 0.35;
    let blob = await new Promise((res) => canvas.toBlob(res, "image/webp", quality));
    while (blob && blob.size > maxBytes && quality > minQuality) {
      quality -= 0.08;
      blob = await new Promise((res) => canvas.toBlob(res, "image/webp", quality));
    }

    // fallback: reduce dimensions gradually if still too large
    if (blob && blob.size > maxBytes && outputSize > 64) {
      let newSize = Math.round(outputSize * 0.85);
      while (newSize >= 64) {
        const c2 = document.createElement("canvas");
        c2.width = newSize;
        c2.height = newSize;
        const ctx2 = c2.getContext("2d");
        ctx2.imageSmoothingEnabled = true;
        ctx2.imageSmoothingQuality = "high";
        ctx2.drawImage(canvas, 0, 0, newSize, newSize);
        const q2 = Math.max(minQuality, quality - 0.05);
        const b2 = await new Promise((res) => c2.toBlob(res, "image/webp", q2));
        if (b2 && b2.size <= maxBytes) return b2;
        newSize = Math.round(newSize * 0.85);
      }
    }

    return blob;
  }

  async function onUploadClick() {
    const DEBUG = false; // ubah ke false atau hapus console.log ketika sudah beres

    try {
      if (!selected || !selected.id) {
        messageRef.current?.add("Pilih umat terlebih dahulu.", "warn");
        setStatus("Pilih umat terlebih dahulu.");
        return;
      }
      if (!imgSrc) {
        messageRef.current?.add("Pilih gambar terlebih dahulu.", "warn");
        setStatus("Pilih gambar terlebih dahulu.");
        return;
      }

      setProcessing(true);
      messageRef.current?.add("Memproses gambar...", "info");
      setStatus("Memproses gambar...");

      // 1) buat image (256px)
      const profileBlob = await produceCroppedBlob(256, 30 * 1024);
      if (!profileBlob) throw new Error("Gagal membuat gambar.");

      // 2) upload file
      const fd = new FormData();
      const kode = selected.umatID ?? selected.kodeUmat ?? String(selected.id);
      const filename = `profile_${kode}.webp`;
      fd.append("files", new File([profileBlob], filename, { type: "image/webp" }));

      messageRef.current?.add("Mengunggah ke server...", "info");
      setStatus("Mengunggah ke server...");
      if (DEBUG) console.log("[UPLOAD] POST /api/upload file:", filename);
      const uploadResult = await apiFetchFormData("/api/upload", fd, { method: "POST" });
      if (DEBUG) console.log("[UPLOAD] raw result:", uploadResult);

      // normalisasi dan ambil numeric file id (media gunakan id)
      const uploadedArr = Array.isArray(uploadResult) ? uploadResult : uploadResult?.data ?? [];
      if (!Array.isArray(uploadedArr) || uploadedArr.length < 1) {
        throw new Error("Upload gagal: tidak ada file di response.");
      }
      const uploadedFile = uploadedArr[0];
      // prefer numeric id for media
      const fileNumericId = uploadedFile?.id ?? uploadedFile?.data?.id ?? uploadedFile?.attributes?.id;
      if (!fileNumericId) throw new Error("Tidak menemukan numeric file id dari upload result.");
      if (DEBUG) console.log("[UPLOAD] fileNumericId:", fileNumericId, "uploadedFile:", uploadedFile);

      // 3) cari entry umat via filter; gunakan populate yang benar untuk media fields
      messageRef.current?.add("Mencari record umat (filter)...", "info");
      setStatus("Mencari record umat (filter)...");
      const tryFilter = async (key, val) => {
        // encode filter dan populate[0]/[1] sesuai format Strapi v5
        const q = `?filters%5B${encodeURIComponent(key)}%5D%5B$eq%5D=${encodeURIComponent(val)}&populate%5B0%5D=profilepic&populate%5B1%5D=avatarpic`;
        const path = `/api/umats/${q}`;
        if (DEBUG) console.log("[FIND] GET", path);
        const resp = await apiFetch(path, { method: "GET" });
        if (DEBUG) console.log("[FIND] raw resp for", key, ":", resp);
        return resp;
      };

      // try internal id first, then umatID
      let findResp = await tryFilter("id", selected.id);
      let foundArr = findResp?.data ?? [];
      if (!Array.isArray(foundArr) || foundArr.length === 0) {
        if (DEBUG) console.log("[FIND] not found by internal id, trying umatID...");
        findResp = await tryFilter("umatID", selected.id);
        foundArr = findResp?.data ?? [];
      }
      if (!Array.isArray(foundArr) || foundArr.length === 0) {
        throw new Error(`Record umat dengan id/umatID='${selected.id}' tidak ditemukan.`);
      }

      const found = foundArr[0];
      if (DEBUG) console.log("[FIND] picked record:", found);

      // 4) gunakan entry documentId untuk path update (Strapi v5 entries referenced by documentId)
      const entryDocumentId = found?.attributes?.documentId ?? found?.documentId ?? found?.id;
      if (!entryDocumentId) throw new Error("Tidak menemukan documentId pada record umat (diperlukan untuk path update).");
      if (DEBUG) console.log("[FIND] entryDocumentId:", entryDocumentId);

      // 5) siapkan PUT body: untuk media gunakan numeric id (tidak documentId)
      const body = {
        data: {
          profilepic: fileNumericId,
          avatarpic: fileNumericId
        }
      };

      if (DEBUG) {
        console.log("[PUT] PUT /api/umats/" + entryDocumentId);
        console.log("[PUT] body:", JSON.parse(JSON.stringify(body)));
      }

      messageRef.current?.add("Menyimpan referensi gambar...", "info");
      setStatus("Menyimpan referensi gambar...");
      const updateResp = await apiFetch(`/api/umats/${entryDocumentId}`, { method: "PUT", body });
      if (DEBUG) console.log("[PUT] updateResp:", updateResp);

      messageRef.current?.add("✅ Sukses: foto tersimpan dan kedua field ter-update.", "info");
      setStatus("Sukses: foto tersimpan.");
    } catch (err) {
      console.error("onUploadClick ERROR:", err);
      messageRef.current?.error("Gagal: " + (err?.message ?? String(err)));
      setStatus("Gagal: " + (err?.message ?? String(err)));
    } finally {
      setProcessing(false);
    }
  }

  // reset transform (fit-to-viewport)
  function resetTransform() {
    setScale(minScale);
    setOffset({ x: 0, y: 0 });
    setRotation(0);
  }

  return (
    <div style={{ padding: 20, maxWidth: 900 }}>
      <h2>Update Foto Umat (Profile & Avatar)</h2>

      <div style={{ margin: "12px 0" }}>
        <label style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>Pilih Umat</label>
        <LookupUmat
          value={selected}
          onChange={(v) => {
            if (lockedKodeUmat) return;
            setSelected(v);
          }}
          initialValue={lockedKodeUmat ? { umatID: lockedKodeUmat } : undefined}
          disabled={!!lockedKodeUmat}
        />
        {lockedKodeUmat && <div style={{ fontSize: 13, color: "#6b7280", marginTop: 6 }}>KodeUmat dikunci: {lockedKodeUmat}</div>}
      </div>

      <div style={{ margin: "12px 0" }}>
        <label style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>Pilih Foto</label>
        <input type="file" accept="image/*" onChange={onFileChange} disabled={!selected || processing} />
        <div style={{ fontSize: 12, color: "#6b7280", marginTop: 6 }}>
          Area crop: {VIEWPORT_SIZE}×{VIEWPORT_SIZE} — apa yang terlihat adalah yang tersimpan. Hasil disimpan WebP, target &lt;30KB.
        </div>
      </div>

      <div style={{ display: "flex", gap: 20 }}>
        <div>
          <div
            ref={containerRef}
            onWheel={onWheel}
            onPointerDown={onPointerDown}
            style={{
              width: VIEWPORT_SIZE,
              height: VIEWPORT_SIZE,
              border: "1px dashed #cbd5e1",
              position: "relative",
              overflow: "hidden",
              touchAction: "none",
              background: "#f8fafc",
            }}
          >
            {imgSrc ? (
              <img
                ref={imgRef}
                src={imgSrc}
                alt="preview"
                draggable={false}
                style={{
                  position: "absolute",
                  left: "50%",
                  top: "50%",
                  width: `${baseDisp.w}px`,
                  height: `${baseDisp.h}px`,
                  transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px)) scale(${scale}) rotate(${rotation}deg)`,
                  transformOrigin: "center center",
                  userSelect: "none",
                  pointerEvents: "none",
                }}
              />
            ) : (
              <div style={{ width: "100%", height: "100%", display: "grid", placeItems: "center", color: "#94a3b8" }}>
                Preview area
              </div>
            )}
            <div style={{ position: "absolute", inset: 0, boxSizing: "border-box", border: "2px solid rgba(255,255,255,0.6)", pointerEvents: "none" }} />
          </div>

          {/* Buttons under viewport */}
          <div style={{ display: "flex", gap: 12, marginTop: 12 }}>
            <button onClick={() => setRotation((r) => (r + 90) % 360)} disabled={!imgSrc || processing}>
              Rotate 90°
            </button>
            <button onClick={resetTransform} disabled={!imgSrc || processing}>
              Reset
            </button>

            <div style={{ flex: 1 }} />

            <button onClick={() => history.goBack()} disabled={processing}>
              Kembali
            </button>
            <button onClick={onUploadClick} disabled={processing || !selected || !imgSrc}>
              {processing ? "Memproses..." : "Simpan"}
            </button>
          </div>

          {/* Zoom slider below buttons */}
          <div style={{ marginTop: 10 }}>
            <label style={{ display: "block", fontSize: 13 }}>Zoom</label>
            <input
              type="range"
              min={minScale}
              max={maxScale}
              step="0.01"
              value={scale}
              onChange={(e) => setScale(Number(e.target.value))}
              disabled={!imgSrc || processing}
              style={{ width: VIEWPORT_SIZE }}
            />
            <div style={{ marginTop: 6, fontSize: 12, color: "#64748b" }}>
              Scale: {scale.toFixed(2)} (min {minScale.toFixed(2)} — max {maxScale.toFixed(2)})
            </div>
          </div>
        </div>

        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600 }}>Petunjuk v.01</div>
          <div style={{ marginTop: 6, color: "#64748b" }}>
            Seret gambar untuk memindahkan posisi crop. Gunakan slider atau scroll untuk zoom-in. <br />
            <strong>scale = 1</strong> berarti seluruh foto ditampilkan dalam kotak crop (fit-to-viewport). Setelah itu, zoom-in bila ingin memperbesar.
          </div>

          {/* single short status (keperluan button / immediate user messages) */}
          {status && <div style={{ marginTop: 8, color: "#0f172a" }}>{status}</div>}

          {/* append-only message log */}
          <MessageLog ref={messageRef} />
        </div>
      </div>
    </div>
  );
}
